import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, Image, FileText, CheckCheck, Check, Smile, Paperclip, Camera, X } from "lucide-react";
import {
  ModernButton,
  Toast,
  ToastType,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui";
import { apiService, WhatsAppChannelSession, WhatsAppMessage } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import { handleWhatsAppError } from "../utils/whatsappErrorHandler";
import { COMMON_CHAT_EMOJIS } from "../constants/commonEmojis";
import { MessageMedia } from "./MessageMedia";
import { buildOutboundTextPayload, getMessageBubbleDisplayParts } from "../utils/messageBubbleText";

interface WhatsAppChatWidgetProps {
  phoneNumber: string;
  leadId?: string;
  className?: string;
}

const DISMISSED_SUGGESTIONS_STORAGE_PREFIX = "mah_whatsapp_dismissed_suggestions_";

function readDismissedSuggestionIdsFromStorage(leadKey: string): string[] {
  try {
    const raw = localStorage.getItem(`${DISMISSED_SUGGESTIONS_STORAGE_PREFIX}${leadKey}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

function writeDismissedSuggestionIdsToStorage(leadKey: string, ids: string[]): void {
  try {
    localStorage.setItem(`${DISMISSED_SUGGESTIONS_STORAGE_PREFIX}${leadKey}`, JSON.stringify(ids));
  } catch {
    void 0;
  }
}

export const WhatsAppChatWidget: React.FC<WhatsAppChatWidgetProps> = ({
  phoneNumber,
  leadId,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [phoneNumberId, setPhoneNumberId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [applyingSuggestion, setApplyingSuggestion] = useState<string | null>(null);
  const [leadSnapshot, setLeadSnapshot] = useState<any | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<string[]>([]);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    helpLink?: string;
    showMetaBadge?: boolean;
  } | null>(null);
  const [channelSession, setChannelSession] = useState<WhatsAppChannelSession | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiService.getWhatsAppConversation(phoneNumber);
      setMessages(result.messages || []);
      if (result.phoneNumberId) {
        setPhoneNumberId(result.phoneNumberId);
      }
      if (result.channelSession) {
        setChannelSession(result.channelSession);
      } else {
        setChannelSession({
          channelSessionStatus: "OPEN",
          allowsSessionMessages: true,
          allowsTemplates: true,
          sessionWindowExpiresAt: null,
          lastCustomerInboundAt: null,
          finalizedAt: null,
        });
      }
    } catch {
      setToast({ message: t("whatsapp.errors.loadMessages"), type: "error" });
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, t]);

  const sessionBlocked =
    channelSession !== null && channelSession.allowsSessionMessages === false;

  const handleFinalizeConversation = async () => {
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }
    try {
      setFinalizing(true);
      await apiService.finalizeWhatsAppConversation(phoneNumber, phoneNumberId);
      setToast({ message: t("whatsapp.session.finalizedToast"), type: "success" });
      await loadMessages();
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMessage"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setFinalizing(false);
    }
  };

  const handleReopenConversation = async () => {
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }
    try {
      setFinalizing(true);
      await apiService.reopenWhatsAppConversation(phoneNumber, phoneNumberId);
      setToast({ message: t("whatsapp.session.reopenedToast"), type: "success" });
      await loadMessages();
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMessage"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setFinalizing(false);
    }
  };

  const loadLeadSnapshot = useCallback(async () => {
    if (!leadId) return;
    try {
      const lead = await apiService.getLead(leadId);
      setLeadSnapshot(lead);
    } catch {
      void 0;
    }
  }, [leadId]);

  useEffect(() => {
    void loadMessages();
    const interval = setInterval(() => {
      void loadMessages();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    void loadLeadSnapshot();
  }, [loadLeadSnapshot]);

  useEffect(() => {
    if (!leadId) {
      setDismissedSuggestions([]);
      return;
    }
    setDismissedSuggestions(readDismissedSuggestionIdsFromStorage(leadId));
  }, [leadId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    const nextHeight = Math.min(textarea.scrollHeight, 160);
    textarea.style.height = `${nextHeight}px`;
  }, [messageText]);

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    if (sessionBlocked) return;
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }

    try {
      setSending(true);
      const payload = buildOutboundTextPayload(user?.name ?? "", messageText);
      await apiService.sendWhatsAppTextMessage({
        phoneNumberId,
        to: phoneNumber,
        message: payload,
        leadId,
      });
      setMessageText("");
      await loadMessages();
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMessage"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const appendEmoji = (emoji: string) => {
    setMessageText((prev) => `${prev}${emoji}`);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (sessionBlocked) return;
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }

    try {
      setUploading(true);
      await apiService.sendWhatsAppMedia({
        phoneNumberId,
        to: phoneNumber,
        type: "document",
        mediaUrl: file,
        caption: messageText || undefined,
        leadId,
      });
      setMessageText("");
      await loadMessages();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMedia"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (sessionBlocked) return;
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }

    try {
      setUploading(true);
      await apiService.sendWhatsAppMedia({
        phoneNumberId,
        to: phoneNumber,
        type: "image",
        mediaUrl: file,
        caption: messageText || undefined,
        leadId,
      });
      setMessageText("");
      await loadMessages();
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMedia"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const latestInboundText = useMemo(() => {
    const inbound = [...messages]
      .reverse()
      .find((message) => message.direction === "INBOUND" && message.type === "TEXT" && message.content);
    return inbound?.content?.toLowerCase() || "";
  }, [messages]);

  const getSuggestionKeywords = useCallback(
    (intent: string): string[] => {
      const raw = t(`whatsapp.suggestionKeywords.${intent}`);
      return raw
        .split("|")
        .map((item) => item.trim().toLowerCase())
        .filter((item) => item.length > 0);
    },
    [t],
  );

  const matchesIntent = useCallback(
    (intent: string) => {
      if (!latestInboundText) return false;
      return getSuggestionKeywords(intent).some((keyword) => latestInboundText.includes(keyword));
    },
    [getSuggestionKeywords, latestInboundText],
  );

  const leadSuggestions = useMemo(() => {
    const suggestions: Array<{
      id: string;
      label: string;
      patch: Record<string, unknown>;
      note: string;
    }> = [];
    if (!leadSnapshot) return suggestions;

    const addSuggestion = (item: { id: string; label: string; patch: Record<string, unknown>; note: string }) => {
      if (!dismissedSuggestions.includes(item.id)) {
        suggestions.push(item);
      }
    };

    const purchaseIntentMap = [
      { key: "purchaseIntentUrgent", value: "urgent" },
      { key: "purchaseIntentResearching", value: "researching" },
      { key: "purchaseIntentComparing", value: "comparing" },
    ] as const;
    const matchedPurchaseIntent = purchaseIntentMap.find((item) => matchesIntent(item.key));
    if (matchedPurchaseIntent && leadSnapshot.purchaseIntent !== matchedPurchaseIntent.value) {
      addSuggestion({
        id: "purchaseIntent",
        label: t("whatsapp.suggestionPurchaseIntent"),
        patch: { purchaseIntent: matchedPurchaseIntent.value },
        note: t("whatsapp.suggestionNotePurchaseIntent"),
      });
    }

    const usageTypeMap = [
      { key: "usageTypeUber", value: "uber" },
      { key: "usageTypeCommercial", value: "commercial" },
      { key: "usageTypeFamily", value: "family" },
      { key: "usageTypePersonal", value: "personal" },
    ] as const;
    const matchedUsageType = usageTypeMap.find((item) => matchesIntent(item.key));
    if (matchedUsageType && leadSnapshot.usageType !== matchedUsageType.value) {
      addSuggestion({
        id: "usageType",
        label: t("whatsapp.suggestionUsageType"),
        patch: { usageType: matchedUsageType.value },
        note: t("whatsapp.suggestionNoteUsageType"),
      });
    }

    const vehicleMomentMap = [
      { key: "vehicleMomentFirstCar", value: "first_car" },
      { key: "vehicleMomentUpgrade", value: "upgrade" },
      { key: "vehicleMomentReplacement", value: "replacement" },
    ] as const;
    const matchedVehicleMoment = vehicleMomentMap.find((item) => matchesIntent(item.key));
    if (matchedVehicleMoment && leadSnapshot.vehicleMoment !== matchedVehicleMoment.value) {
      addSuggestion({
        id: "vehicleMoment",
        label: t("whatsapp.suggestionVehicleMoment"),
        patch: { vehicleMoment: matchedVehicleMoment.value },
        note: t("whatsapp.suggestionNoteVehicleMoment"),
      });
    }

    if (matchesIntent("hasTradeIn") && !leadSnapshot.hasTradeIn) {
      addSuggestion({
        id: "hasTradeIn",
        label: t("whatsapp.suggestionTradeIn"),
        patch: { hasTradeIn: true },
        note: t("whatsapp.suggestionNoteTradeIn"),
      });
    }

    if (matchesIntent("wantsFinancing") && !leadSnapshot.wantsFinancing) {
      addSuggestion({
        id: "wantsFinancing",
        label: t("whatsapp.suggestionFinancing"),
        patch: { wantsFinancing: true },
        note: t("whatsapp.suggestionNoteFinancing"),
      });
    }

    return suggestions.slice(0, 6);
  }, [dismissedSuggestions, leadSnapshot, matchesIntent, t]);

  const persistDismissedSuggestionIds = useCallback((ids: string[]) => {
    if (!leadId) return;
    writeDismissedSuggestionIdsToStorage(leadId, ids);
  }, [leadId]);

  const dismissLeadSuggestion = useCallback(
    (suggestionId: string) => {
      setDismissedSuggestions((previous) => {
        if (previous.includes(suggestionId)) return previous;
        const next = [...previous, suggestionId];
        persistDismissedSuggestionIds(next);
        return next;
      });
    },
    [persistDismissedSuggestionIds],
  );

  const applyLeadSuggestion = async (suggestion: { id: string; patch: Record<string, unknown>; note: string }) => {
    if (!leadId) return;
    try {
      setApplyingSuggestion(suggestion.id);
      const lead = leadSnapshot || (await apiService.getLead(leadId));
      const currentNotes = (lead?.notes || "").trim();
      const mergedNotes = currentNotes ? `${currentNotes}\n${suggestion.note}` : suggestion.note;
      await apiService.updateLead(leadId, {
        notes: mergedNotes,
        ...suggestion.patch,
      });
      setLeadSnapshot((previous: any) => ({
        ...(previous || {}),
        ...suggestion.patch,
        notes: mergedNotes,
      }));
      setDismissedSuggestions((previous) => {
        if (previous.includes(suggestion.id)) return previous;
        const next = [...previous, suggestion.id];
        persistDismissedSuggestionIds(next);
        return next;
      });
      setToast({ message: t("whatsapp.suggestionApplied"), type: "success" });
    } catch (error) {
      const errorData = handleWhatsAppError(error, t, t("whatsapp.errors.sendMessage"));
      setToast({ ...errorData, type: "error" });
    } finally {
      setApplyingSuggestion(null);
    }
  };

  const retryFailedMessage = async (message: WhatsAppMessage) => {
    if (message.direction !== "OUTBOUND" || message.status !== "FAILED") return;
    if (retryingMessageId === message.id) return;
    if (!phoneNumberId) {
      setToast({ message: t("whatsapp.errors.configNotLoaded"), type: "error" });
      return;
    }

    try {
      setRetryingMessageId(message.id);

      if (message.type === "TEXT") {
        await apiService.sendWhatsAppTextMessage({
          phoneNumberId,
          to: phoneNumber,
          message: message.content,
          leadId,
        });
      } else {
        const mediaTypeMap: Record<"IMAGE" | "VIDEO" | "DOCUMENT", "image" | "video" | "document"> = {
          IMAGE: "image",
          VIDEO: "video",
          DOCUMENT: "document",
        };

        if (!message.mediaUrl || !(message.type in mediaTypeMap)) {
          setToast({ message: t("whatsapp.retryMediaUnavailable"), type: "warning" });
          return;
        }

        await apiService.sendWhatsAppMediaMessage({
          phoneNumberId,
          to: phoneNumber,
          mediaType: mediaTypeMap[message.type as "IMAGE" | "VIDEO" | "DOCUMENT"],
          mediaUrl: message.mediaUrl,
          caption: message.caption || undefined,
          filename: message.filename || undefined,
          leadId,
        });
      }

      await loadMessages();
      setToast({ message: t("whatsapp.retrySent"), type: "success" });
    } catch (error) {
      const fallback =
        message.type === "TEXT" ? t("whatsapp.errors.sendMessage") : t("whatsapp.errors.sendMedia");
      const errorData = handleWhatsAppError(error, t, fallback);
      setToast({ ...errorData, type: "error" });
    } finally {
      setRetryingMessageId(null);
    }
  };

  const getStatusIcon = (message: WhatsAppMessage) => {
    if (message.direction === "INBOUND") return null;

    switch (message.status) {
      case "SENT":
        return <Check className="h-3 w-3 text-gray-400" />;
      case "DELIVERED":
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case "READ":
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      case "FAILED":
        return (
          <button
            type="button"
            onClick={() => void retryFailedMessage(message)}
            disabled={retryingMessageId === message.id}
            className="text-xs text-red-500 hover:text-red-600 disabled:opacity-60"
            title={t("whatsapp.retryAction")}
            aria-label={t("whatsapp.retryAction")}
          >
            {retryingMessageId === message.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              t("whatsapp.retryLabel")
            )}
          </button>
        );
      default:
        return <Loader2 className="h-3 w-3 text-gray-400 animate-spin" />;
    }
  };

  const getMessageIcon = (type: string) => {
    switch (type) {
      case "IMAGE":
        return <Image className="h-4 w-4" />;
      case "DOCUMENT":
        return <FileText className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-[600px] border rounded-lg ${className}`}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          helpLink={toast.helpLink}
          showMetaBadge={toast.showMetaBadge}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">{t("whatsapp.noMessages")}</p>
          </div>
        ) : (
          messages.map((message) => {
            const display = getMessageBubbleDisplayParts(message);
            return (
              <div
                key={message.id}
                className={`flex ${message.direction === "OUTBOUND" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    message.direction === "OUTBOUND"
                      ? "bg-blue-500 text-white"
                      : "bg-white dark:bg-gray-800 text-foreground"
                  }`}
                >
                  {message.type !== "TEXT" && (
                    <div className="flex items-center gap-2 mb-1 text-xs opacity-75">
                      {getMessageIcon(message.type)}
                      <span>{t(`whatsapp.messageTypes.${message.type.toLowerCase()}`)}</span>
                    </div>
                  )}

                  {message.mediaUrl && (
                    <div className="mb-2">
                      <MessageMedia
                        type={message.type}
                        mediaUrl={message.mediaUrl}
                        caption={message.caption || undefined}
                        filename={message.filename || undefined}
                        direction={message.direction}
                      />
                    </div>
                  )}

                  {display.header && (
                    <p
                      className={`text-xs font-semibold mb-1 pb-1 border-b ${
                        message.direction === "OUTBOUND"
                          ? "opacity-95 border-white/30"
                          : "opacity-90 border-border"
                      }`}
                    >
                      {display.header}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words text-sm [font-variant-emoji:emoji]">
                    {display.body}
                  </p>

                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-xs opacity-75">
                      {new Date(message.createdAt).toLocaleTimeString(i18n.language, {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {getStatusIcon(message)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-border p-3 bg-card">
        {channelSession && sessionBlocked && (
          <div className="mb-2 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground">
            {channelSession.channelSessionStatus === "CLOSED"
              ? t("whatsapp.session.closedBanner")
              : t("whatsapp.session.templateOnlyBanner")}
          </div>
        )}
        <div className="flex justify-end gap-2 mb-2">
          {channelSession && channelSession.channelSessionStatus !== "CLOSED" && (
            <ModernButton
              type="button"
              variant="outline"
              size="sm"
              disabled={finalizing || loading}
              onClick={() => void handleFinalizeConversation()}
            >
              {t("whatsapp.session.finalize")}
            </ModernButton>
          )}
          {channelSession && channelSession.channelSessionStatus === "CLOSED" && (
            <ModernButton
              type="button"
              variant="outline"
              size="sm"
              disabled={finalizing || loading}
              onClick={() => void handleReopenConversation()}
            >
              {t("whatsapp.session.reopen")}
            </ModernButton>
          )}
        </div>
        {leadId && leadSuggestions.length > 0 && (
          <div className="mb-2">
            <p className="text-xs text-muted-foreground mb-1">{t("whatsapp.suggestionTitle")}</p>
            <div className="flex flex-wrap gap-1.5">
              {leadSuggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="inline-flex items-stretch rounded-md border border-border overflow-hidden bg-card"
                >
                  <button
                    type="button"
                    onClick={() => void applyLeadSuggestion(suggestion)}
                    disabled={applyingSuggestion === suggestion.id}
                    className="text-xs px-2 py-1 hover:bg-muted disabled:opacity-60 text-left"
                  >
                    {applyingSuggestion === suggestion.id ? t("whatsapp.suggestionApplying") : suggestion.label}
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissLeadSuggestion(suggestion.id);
                    }}
                    className="px-1.5 flex items-center justify-center border-l border-border text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
                    aria-label={t("whatsapp.suggestionDismiss")}
                    title={t("whatsapp.suggestionDismiss")}
                  >
                    <X className="h-3.5 w-3.5 stroke-[1.5]" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <ModernButton
                type="button"
                variant="outline"
                size="sm"
                disabled={sending || sessionBlocked}
                className="shrink-0 px-2.5"
                aria-label={t("whatsapp.emojiPicker")}
              >
                <Smile className="h-5 w-5 stroke-[1.5]" />
              </ModernButton>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="top"
              className="w-[min(100vw-2rem,20rem)] p-2 border-border bg-popover"
            >
              <div className="max-h-52 overflow-y-auto grid grid-cols-8 gap-0.5">
                {COMMON_CHAT_EMOJIS.map((emoji, index) => (
                  <button
                    key={`${emoji}-${index}`}
                    type="button"
                    className="text-xl leading-none p-1.5 rounded-md hover:bg-muted transition-colors"
                    onClick={() => appendEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <ModernButton
            type="button"
            variant="outline"
            size="sm"
            disabled={sending || uploading || sessionBlocked}
            className="shrink-0 px-2.5"
            aria-label={t("whatsapp.attachFile")}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-5 w-5 stroke-[1.5]" />
          </ModernButton>
          <ModernButton
            type="button"
            variant="outline"
            size="sm"
            disabled={sending || uploading || sessionBlocked}
            className="shrink-0 px-2.5"
            aria-label={t("whatsapp.attachImage")}
            onClick={() => imageInputRef.current?.click()}
          >
            <Camera className="h-5 w-5 stroke-[1.5]" />
          </ModernButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={handleFileUpload}
            className="hidden"
          />
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <textarea
            ref={messageTextareaRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={t("whatsapp.typeMessage")}
            disabled={sending || uploading || sessionBlocked}
            rows={1}
            className="flex-1 block w-full px-3 py-2.5 border rounded-md shadow-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent disabled:bg-surface disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors duration-150 border-border resize-none overflow-y-auto max-h-40 min-h-[46px]"
          />
          <ModernButton
            onClick={handleSendMessage}
            disabled={sending || uploading || sessionBlocked || !messageText.trim()}
          >
            {sending || uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </ModernButton>
        </div>
      </div>
    </div>
  );
};
